/*
  Реестр должников — §3.3.6, §4.9.

  Фильтры и порядок живут в адресе страницы: ссылку можно переслать коллеге, и
  он увидит ровно тот же экран. Первый уровень отбора — рабочая зона, дальше
  подробности, как и предписано §3.3.6.

  По умолчанию порядок строк задаёт не таблица, а триаж: сначала то, что горит,
  внутри приоритета — крупное.

  🔴 Сортировка по колонке добавлена 14.09.2026 по указанию владельца; до этого
  её сознательно не делали, чтобы порядок триажа не терялся. Опасение осталось в
  силе, поэтому сортировка — ЯВНЫЙ выбор: умолчание прежнее, выбранный порядок
  виден стрелкой, и рядом с таблицей стоит возврат к порядку по важности.
*/

import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { api } from '../api/client.js'
import { can, has } from '../components/access.js'
import {
  AMOUNT_TITLES,
  CONTACT_TITLES,
  EXPIRED_TITLES,
  LIMITATION_TITLES,
  ROUTE_TITLES,
  STAGE_TITLES,
  WORK_TITLES,
  ZONE_TITLES,
  accounts,
} from '../api/format.js'
import { Empty, Loading, Notice } from '../components/Common.jsx'
import RegistryFilters from '../components/RegistryFilters.jsx'
import PageBar from '../components/PageBar.jsx'
import PickWhole from '../components/PickWhole.jsx'
import RegistrySelection from '../components/RegistrySelection.jsx'
import RegistryTable from '../components/RegistryTable.jsx'
import useRegistryPick from '../components/useRegistryPick.js'
import useServerPaging from '../components/useServerPaging.js'

/*
  Куда упорядочить при ПЕРВОМ нажатии на колонку. Деньги — по убыванию: человек
  ищет крупное, а не счета на сто рублей. Номер и давность — по возрастанию:
  номер ищут глазами сверху вниз, а по давности первым нужен тот, чей долг
  сгорит раньше.
*/
const FIRST_DIRECTION = { number: 'asc', amount: 'desc', limitation: 'asc' }

// Наборы подписей передаются в отбор одним объектом: иначе список полей в
// `RegistryFilters` пришлось бы держать двумя параллельными списками - имён и
// подписей, - и они разъехались бы при первом же новом условии.
const FILTER_TITLES = {
  ZONE: ZONE_TITLES,
  STAGE: STAGE_TITLES,
  WORK: WORK_TITLES,
  LIMITATION: LIMITATION_TITLES,
  EXPIRED: EXPIRED_TITLES,
  AMOUNT: AMOUNT_TITLES,
  CONTACT: CONTACT_TITLES,
  ROUTE: ROUTE_TITLES,
}

// Профиль без прав - реестр без выбора строк, каким он был до 16.10.2026.
const NO_RIGHTS = { permissions: [] }

export default function RegistryPage({ profile = NO_RIGHTS, onBurning }) {
  const [params, setParams] = useSearchParams()
  const [page, setPage] = useState(null)
  const [error, setError] = useState(null)
  // Итог действия над выбранными или удаления из карточки (16.10.2026); версия
  // перечитывает список. Сообщение из карточки сразу убирается из истории
  // браузера - иначе обновление страницы повторило бы «счёт удалён».
  const location = useLocation()
  const navigate = useNavigate()
  const [notice, setNotice] = useState(location.state?.notice ?? null)
  const [version, setVersion] = useState(0)
  useEffect(() => {
    if (location.state?.notice) {
      navigate(`${location.pathname}${location.search}`, { replace: true, state: null })
    }
  }, [location, navigate])
  const pick = useRegistryPick(params)
  // Выбор строк виден и в режиме «только чтение» - закрывает его `RegistrySelection`
  // (кнопки серые, причина написана), поэтому здесь `has`, а не `can`.
  const canPick = ['manage_stages', 'delete_accounts'].some((name) => has(profile, name))

  const offset = Number(params.get('offset') ?? 0)
  // Отбор без страницы - то, что берёт «Выбрать весь отбор» (23.10.2026).
  const filterQuery = Object.fromEntries([...params.entries()].filter(([name]) => name !== 'offset'))
  /*
    21.10.2026: размер страницы выбирается (10, 25, 50, 100) и запоминается один на
    все списки; страницы по-прежнему режет сервер по смещению в адресе. Размер в
    адрес не пишется: это привычка браузера, а не часть отбора, и ссылка,
    пересланная коллеге, не должна навязывать ему свой размер. Смещение не
    меняем, если оно то же: иначе повторное «0» писало бы в историю браузера
    лишнюю запись.
  */
  const { size, bar } = useServerPaging(offset, page?.total ?? 0, (next) => {
    if (next !== offset) setFilter('offset', next ? String(next) : null)
  })

  useEffect(() => {
    const query = Object.fromEntries(params.entries())
    /*
      Ответ на УСТАРЕВШИЙ запрос выбрасывается.

      Полный разбор портфеля считается на каждое обращение, и широкий отбор
      считается заметно дольше узкого. При быстрой смене фильтров ответы
      приходят не в том порядке, в каком их просили: сначала узкий, следом
      прежний широкий, - и на экране оседает результат ПРЕДЫДУЩЕГО отбора,
      хотя в полях стоит новый. Найдено 09.09.2026 разбором жалобы владельца
      «иногда не подгружает, потом когда запрос доходит подгружает».
    */
    let current = true
    setPage(null)
    // Прошлая неудача снимается вместе с новым запросом: без этого экран
    // навсегда оставался блоком ошибки, даже когда сервер уже отвечал.
    setError(null)
    api
      .registry({ ...query, limit: size, offset })
      .then((result) => {
        if (!current) return
        // Страница за концом (23.10.2026, находка проверки): после удаления или смены стадии
        // на последней странице счетов стало меньше - переходим на последнюю существующую, а
        // не показываем «ни один счёт не подошёл» без панели страниц.
        if (result.rows.length === 0 && result.total > 0 && offset > 0) {
          const next = new URLSearchParams(params)
          const last = Math.floor((result.total - 1) / size) * size
          if (last) next.set('offset', String(last))
          else next.delete('offset')
          setParams(next, { replace: true })
          return
        }
        setPage(result)
        // Число «горит» для значка в меню приходит с сервера и посчитано по
        // ВСЕМУ портфелю. Считать его здесь по `result.rows` нельзя: это
        // строки одной страницы одного отбора, и значок гас на второй
        // странице реестра, хотя портфель не менялся.
        if (onBurning) onBurning(result.burning)
      })
      .catch((failure) => {
        if (current) setError(failure)
      })
    return () => {
      current = false
    }
  }, [params, setParams, offset, size, onBurning, version])

  /*
    Правка адреса страницы сразу несколькими значениями: порядок задаётся парой
    «поле и направление», а два вызова подряд затёрли бы друг друга - каждый
    считает от `params`, которые в этот момент ещё прежние.

    Смена отбора всегда возвращает на первую страницу: строки другие, и
    «страница 3» из прошлого отбора означает пустой экран. Но САМА смена
    страницы offset не сбрасывает - до 14.09.2026 сбрасывала, и кнопки «Вперёд»
    и «Назад» не листали вовсе.
  */
  function setFilters(changes) {
    const next = new URLSearchParams(params)
    for (const [name, value] of Object.entries(changes)) {
      if (value) next.set(name, value)
      else next.delete(name)
    }
    if (!('offset' in changes)) next.delete('offset')
    setParams(next)
  }

  function setFilter(name, value) {
    setFilters({ [name]: value })
  }

  /*
    Нажатие на ту же колонку переворачивает порядок, на другую - задаёт её со
    своим умолчанием. Отсчёт идёт от ПРИМЕНЁННОГО сервером порядка, а не от
    того, что кабинет когда-то попросил: свой ответ на этот вопрос разошёлся бы
    с серверным на первом же умолчании.
  */
  function toggleSort(field) {
    const current = page?.sort ?? 'priority'
    const currentDirection = page?.direction ?? 'asc'
    const direction =
      current === field ? (currentDirection === 'desc' ? 'asc' : 'desc') : FIRST_DIRECTION[field]
    setFilters({ sort: field, direction })
  }

  /*
    Ошибка списка не стирает экран (аудит 26.10.2026, причина 1): шапка и фильтры
    остаются, сбой встаёт на место таблицы, и отбор можно сменить, не
    перезагружая страницу.
  */
  return (
    <>
      <header className="head">
        <div>
          <h1 className="head__title">Реестр должников</h1>
          <div className="head__note">
            {page ? `Найдено ${accounts(page.total)}` : error ? '' : 'Считаем портфель…'}
          </div>
        </div>
        {/* Заводить счёт руками может тот, кто подаёт заявления (20.10.2026): файл
            остаётся основным путём, форма - для тех, у кого выгрузки нет. */}
        {can(profile, 'build_documents') ? (
          <Link className="button" to="/accounts/new">
            Добавить счёт
          </Link>
        ) : null}
      </header>

      <RegistryFilters
        params={params}
        onChange={setFilter}
        /* Порядок одной строкой «поле:направление» - так его выбирают на
           телефоне, где заголовков колонок не видно вовсе. */
        onOrder={(value) => {
          const [sort, direction] = value ? value.split(':') : [null, null]
          setFilters({ sort, direction })
        }}
        titles={FILTER_TITLES}
      />

      {notice ? <Notice kind={notice.kind}>{notice.text}</Notice> : null}
      {error ? <Notice error={error} wide /> : null}
      {!page && !error ? <Loading what="Считаем долг, пени и сроки по каждому счёту" /> : null}

      {page && page.rows.length === 0 ? (
        <Empty title="Под эти условия не подошёл ни один счёт">
          Смягчите отбор или очистите фильтры. Всего в портфеле{' '}
          {accounts(Object.values(page.zones).reduce((sum, count) => sum + count, 0))}.
        </Empty>
      ) : null}

      {page && page.rows.length > 0 ? (
        <>
          {/*
            Возврат к порядку по важности виден только тогда, когда порядок
            изменён: кнопка, которая ничего не меняет, заставляет гадать, что
            она делает.
          */}
          {page.sort && page.sort !== 'priority' ? (
            <div className="row">
              <span className="muted">Порядок выбран вручную.</span>
              <button
                type="button"
                className="button button--quiet"
                onClick={() => setFilters({ sort: null, direction: null })}
              >
                Вернуть порядок по важности
              </button>
            </div>
          ) : null}

          {/* Решение владельца 23.10.2026: весь отбор - с сервера, тем же фильтром. */}
          {canPick ? (
            <PickWhole
              total={page.total}
              shown={page.rows.length}
              filterKey={JSON.stringify(filterQuery)}
              fetchAll={() => api.registrySelection(filterQuery)}
              onPicked={(items) => pick.toggleAll(items, true)}
              selected={pick.list.length}
            />
          ) : null}

          <div className="card table-scroll">
            <RegistryTable
              rows={page.rows}
              sort={page.sort}
              direction={page.direction}
              onSort={toggleSort}
              pick={canPick ? pick : undefined}
            />
          </div>

          <PageBar {...bar} what="счетов" />
        </>
      ) : null}

      {canPick ? (
        <RegistrySelection
          pick={pick}
          profile={profile}
          onDone={(done) => {
            setNotice(done)
            setVersion((value) => value + 1)
          }}
        />
      ) : null}
    </>
  )
}

